import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  GridOverlay,
  type GridSelectionModel,
  type GridSortModel,
} from "@mui/x-data-grid-pro";
import { DataGrid } from "@/components/DataGrid";
import type {
  InheritanceReuseCandidateRow,
  InheritanceReuseCandidateEntity,
} from "@/features/inheritanceReuse/types";
import { makeInheritanceReuseColumns } from "@/features/inheritanceReuse/utils/makeColumns";
import { ROWS_PER_PAGE_OPTIONS } from "@/features/inheritanceReuse/utils/constants";

/** 0 件時の専用空状態（他機能と揃えた控えめな中央表示）。 */
const NoRowsOverlay: React.FC = () => (
  <GridOverlay>
    <Box sx={{ py: 4, textAlign: "center" }}>
      <Typography variant="body2" color="text.secondary">
        条件に一致する候補が見つかりませんでした。
      </Typography>
    </Box>
  </GridOverlay>
);

const GRID_COMPONENTS = { NoRowsOverlay };

interface IProps {
  candidates: InheritanceReuseCandidateEntity[];
  rowCount: number;
  loading: boolean;
  page: number;
  pageSize: number;
  sortModel: GridSortModel;
  selectionModel: GridSelectionModel;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSortModelChange: (model: GridSortModel) => void;
  onSelectionModelChange: (model: GridSelectionModel) => void;
  onAcquire: (row: InheritanceReuseCandidateRow) => void;
}

/**
 * 相続後リユース候補テーブル（DataGridPro のサーバサイドページング＋ソート）。
 * 行 id は候補エンティティの数値 id をそのまま用いる。
 * BE の ordering は単一キーのみ対応のため disableMultipleColumnsSorting で複数列ソートを禁止する。
 */
export const InheritanceReuseDataGrid: React.FC<IProps> = ({
  candidates,
  rowCount,
  loading,
  page,
  pageSize,
  sortModel,
  selectionModel,
  onPageChange,
  onPageSizeChange,
  onSortModelChange,
  onSelectionModelChange,
  onAcquire,
}) => {
  const columns = useMemo(
    () => makeInheritanceReuseColumns(onAcquire),
    [onAcquire]
  );

  return (
    <DataGrid
      rows={candidates}
      columns={columns}
      loading={loading}
      stickyHeader
      checkboxSelection
      keepNonExistentRowsSelected
      selectionModel={selectionModel}
      onSelectionModelChange={onSelectionModelChange}
      paginationMode="server"
      sortingMode="server"
      disableMultipleColumnsSorting
      rowCount={rowCount}
      page={page}
      pageSize={pageSize}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      disableSelectionOnClick
      disableColumnFilter
      components={GRID_COMPONENTS}
      // 根拠チップや複数所有者で行が縦に伸びるため自動行高にする。
      getRowHeight={() => "auto"}
      headerHeight={44}
      sx={{
        // 自動行高でもセル内に上下の余白を確保し、上質な行間にする。
        "& .MuiDataGrid-cell": { py: 1 },
        "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 },
      }}
    />
  );
};
